import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from "react-router-dom";
import geoLogo from '../assets/Images/geo-logo-glossy.png';
import RoleGuard, { CM_ROLES } from './RoleGuard';

// Issue 9b — dark background while lazy chunks load, now with the animated
// GEO mark instead of a blank screen.
const PageLoader = () => (
  <div style={{
    minHeight: '100vh', background: '#0D1117',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <img
      src={geoLogo}
      alt="Loading"
      style={{ width: '110px', animation: 'geo-loader-pulse 1.8s ease-in-out infinite' }}
    />
    <style>{`
      @keyframes geo-loader-pulse {
        0%, 100% { transform: scale(1); filter: drop-shadow(0 0 6px rgba(59,130,246,0.35)); }
        50% { transform: scale(1.08); filter: drop-shadow(0 0 18px rgba(59,130,246,0.75)); }
      }
    `}</style>
  </div>
);

// Lazy page imports
const NewsDetail = lazy(() => import("../Pages/News/NewsDetail"));
// Client proof-of-concept demo (3D Virtual Shelf / "Native Twin") — isolated
// page, dummy data only, no real store/product wiring. See memory note
// project_videostore_3d_shelf_native_twin.md for context.
const Demo3DShelf = lazy(() => import("../Pages/Demo3DShelf"));
// Flat (non-3D), normal e-commerce-style alternative to Demo3DShelf.js —
// same real product data and the same live-camera AI tap-to-identify, just
// a product grid + detail card instead of a rendered 3D scene. Requested
// after the 3D version, kept as a separate page (Demo3DShelf.js untouched).
const DemoFlatStore = lazy(() => import("../Pages/DemoFlatStore"));
// Owner-side setup tool proof-of-concept: upload one shelf photo, manually
// tag products on it — the reliable, one-time-setup alternative to
// real-time AI shelf detection. Isolated, local-only, own route.
const DemoShelfSetup = lazy(() => import("../Pages/DemoShelfSetup"));
// Hover-to-identify proof of concept: runs coco-ssd over a live camera or an
// uploaded video and names whatever the cursor is over, with no tagging step
// first. Standalone — the Video Store's own ShelfProductDetect is untouched.
const DemoHoverDetect = lazy(() => import("../Pages/DemoHoverDetect"));
const RealityShows = lazy(() => import("../Pages/More/RealityShows"));
const RealityShowsDetails = lazy(() => import("../Pages/More/RealityShowsDetails"));
const ComedyShows = lazy(() => import("../Pages/More/ComedyShows"));
const SocialnEngagementDetails = lazy(() => import("../Pages/SocialnEngagementDetails"));
const ComedyShowsDetails = lazy(() => import("../Pages/More/ComedyShowsDetails"));
const ListenSelect = lazy(() => import("../Pages/More/ListenSelect"));
const SocialnEngagement = lazy(() => import("../Pages/SocialnEngagement"));
const WatchlistSelect = lazy(() => import("../Pages/More/WatchlistSelect"));
const HomeLivestream = lazy(() => import("../Pages/HomeLivestream"));
const LivestreamDetail = lazy(() => import("../Pages/LivestreamDetail"));
const AudioVideo = lazy(() => import("../Pages/More/AudioVideo"));
const Music = lazy(() => import("../Pages/More/Music"));
const World = lazy(() => import("../Pages/World"));
const VOD = lazy(() => import("../Pages/More/VOD"));
const Comedy = lazy(() => import("../Pages/More/Comedy"));
const Drama = lazy(() => import("../Pages/More/Drama"));
const Series = lazy(() => import("../Pages/More/Series"));
const PhysicalStudio = lazy(() => import("../Pages/PhysicalStudio"));
const VirtualStudio = lazy(() => import("../Pages/VirtualStudio"));
const ComedyShowsDetail = lazy(() => import("../Pages/More/ComedyShowsDetail"));
const SeriesDetail = lazy(() => import("../Pages/More/SeriesDetail"));
const DramaDetail = lazy(() => import("../Pages/More/DramaDetail"));
const LiveStream = lazy(() => import("../Pages/LiveStream"));
const GeoMeet = lazy(() => import("../Pages/GeoMeet"));
const Aboutus = lazy(() => import("../Pages/OtherPages/Aboutus"));
const PrivacyPolicy = lazy(() => import("../Pages/OtherPages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("../Pages/OtherPages/TermsConditions"));
const Newsletter = lazy(() => import("../Pages/OtherPages/Newsletter"));
const Contentcreate = lazy(() => import("../Pages/Content/Contentcreate"));
const ContentList = lazy(() => import("../Pages/Content/ContentList"));
const Dashboard = lazy(() => import("../Pages/Dashboard"));
const OneToOneCall = lazy(() => import("../Pages/OneToOneCall"));
const OneToOneRoom = lazy(() => import("../Pages/OneToOneRoom"));
const Restaurants = lazy(() => import("../Pages/OtherPages/Restaurants"));
const Bankarena = lazy(() => import("../Pages/OtherPages/Bankarena"));
const Gallery = lazy(() => import("../Pages/JoinLive/Gallery"));
const JoinLive = lazy(() => import("../Pages/JoinLive/JoinLive"));
const MainLayout = lazy(() => import("../Pages/VideoStore/Manager/MainLayout"));
const ManagerLoginPage = lazy(() => import("../Pages/Landing/ManagerLoginPage"));
const AgentLoginPage = lazy(() => import("../Pages/Landing/AgentLoginPage"));
const ManagerSetPassword = lazy(() => import("../Pages/Landing/ManagerSetPassword"));
const AgentSetPassword = lazy(() => import("../Pages/Landing/AgentSetPassword"));
const CreateStore = lazy(() => import("../Pages/VideoStore/StoreComponent/Popup/CreateStore"));
const Landing = lazy(() => import("../Pages/Landing/Landing"));
const LandingVideo = lazy(() => import("../Pages/Landing/LandingVideo"));
const States = lazy(() => import("../Pages/states"));
const AdminLoginFlow = lazy(() => import("../Pages/ContentManager/AdminLoginFlow"));
const Agent = lazy(() => import("../Pages/VideoStore/Agent/Agent"));
const CameraIP = lazy(() => import("../Pages/VideoStore/StoreComponent/Popup/CameraIP"));
const RestaurantDetails = lazy(() => import("../Pages/restaurant/Restaurant"));
const RegisterRestaurant = lazy(() => import("../Pages/restaurant/RestaurantRegister"));
const MenuManager = lazy(() => import("../Pages/restaurant/MenuManager"));
const MainAdmin = lazy(() => import("../Pages/VideoStore/Admin/MainAdmin"));
const OrderSummary = lazy(() => import("../Pages/VideoStore/StoreComponent/NotUse/VideoCashier"));
const Invoice = lazy(() => import("../Pages/VideoStore/StoreComponent/NotUse/VideoInvoice"));
const Buyer = lazy(() => import("../Pages/VideoStore/Buyer/Buyer"));
const VisdeoStoreList = lazy(() => import("../Pages/VideoStore/StoreComponent/Popup/VisdeoStoreList"));
const Cashier = lazy(() => import("../Pages/VideoStore/Cashier/Cashier"));
const RemoteMobile = lazy(() => import("../Pages/Landing/RemoteMobile"));
const OneToManyRoom = lazy(() => import("../Pages/OneToManyRoom"));
const Flutterwave = lazy(() => import("../Pages/VideoStore/VideoStoreCSS/FlutterWave/FlutterWave"));
const PaymentSuccessful = lazy(() => import("../Pages/PaymentSuccessful"));
const CryPtoPayment = lazy(() => import("../Pages/CryPtoPayment"));
const OrderTracking = lazy(() => import("../Pages/OrderTracking/OrderTracking"));
const DriverDashboard = lazy(() => import("../Pages/OrderTracking/DriverDashboard"));
const RestaurantAgent = lazy(() => import("../Pages/restaurant/RestaurantAgent"));
const RestaurantOwner = lazy(() => import("../Pages/restaurant/RestaurantOwner"));
const RestaurantManager = lazy(() => import("../Pages/restaurant/RestaurantManager"));
const RestaurantBuyer = lazy(() => import("../Pages/restaurant/RestaurantBuyer"));
const Broadcaster = lazy(() => import("../Pages/Broadcaster"));
const Viewer = lazy(() => import("../Pages/Viewer"));
const StateLgawel = lazy(() => import("../components/StateLgawel"));

const GlobalDashboard = lazy(() => import("../Pages/ContentManager/GlobalAdmin/GlobalDashboard"));
const MasterAdminDashboard = lazy(() => import("../Pages/ContentManager/MasterAdmin/MasterAdminDashboard"));
const SuperAdminDashboard = lazy(() => import("../Pages/ContentManager/SuperAdmin/SuperAdminDashboard"));
const CmLanding = lazy(() => import("../Pages/Landing/CmLanding"));
const Statepage = lazy(() => import("../components/StateLGAUi"));
const CNIlandingpage = lazy(() => import("../Pages/ContentManager/CNIlandingpage"));
const MainSiteHome = lazy(() => import("../Pages/Landing/MainSiteHome"));
const SiteAdmin = lazy(() => import("../Pages/SiteAdmin"));
// STAGE only — plays the GEO intro before the hub's cards (client, 27 Oct).
const StageIntroVideo = lazy(() => import("../Pages/Landing/StageIntroVideo"));

function AppRoutes() {
  const currentModule = process.env.REACT_APP_MODULE;

  // Client-facing preview build. Shows only the Community (Impact) and
  // Office Front cards — Landing.js filters the card list and CardWithIframe
  // opens Office Front directly. Routes are limited to what those two cards
  // actually reach, so nothing else on the platform is discoverable from
  // this domain even by typing a path. The MAIN, CM and default branches
  // below are untouched.
  if (currentModule === "STAGE") {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* The hub itself — the two cards live here, behind the GEO intro
              on the first visit of a session (see StageIntroVideo). Wrapped
              rather than given its own route so the hub keeps the "/" path
              the cards and every redirect below already point at. */}
          <Route
            path="/"
            element={
              <StageIntroVideo>
                <Landing />
              </StageIntroVideo>
            }
          />
          {/* Open/close switch for this build. Stays reachable while the
              site is closed (see StageAccessGate), or there would be no
              way back in. */}
          <Route path="/site-admin" element={<SiteAdmin />} />
          {/* The Impact card embeds the state/LGA picker at "/home" in an
              overlay (Broadcaster.js's stateLGAOverlaySrc) and a CM-role
              user's studio at "/news-livestream", so both must exist. */}
          <Route path="/home" element={<MainSiteHome />} />
          <Route path="/news-livestream" element={<Broadcaster />} />
          {/* A viewer following a ?state=&lga= link from a simulcast lands
              here, same as the other builds. */}
          <Route path="/News" element={<NewsDetail />} />
          <Route path="/joinLive" element={<JoinLive />} />
          {/* Broadcast room's One to One / One to Many buttons. */}
          <Route path="/OneToOneCall" element={<OneToOneCall />} />
          <Route path="/OneToManyCall" element={<OneToManyRoom />} />
          <Route path="/OneToOneRoom" element={<OneToOneRoom />} />
          {/* Anything else — including every path belonging to a card this
              build does not show — returns to the hub. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }


  // geomedium.com's own build — fully separate from both the CM branch below
  // and the default (geo.geomedium.com) branch further down, so neither is
  // touched by this domain's flow.
  if (currentModule === "MAIN") {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingVideo />} />
          <Route path="/home" element={<MainSiteHome />} />
          {/* Reuses geo.geomedium.com's own hub dashboard (Video Stores,
              Restaurants, Bank Arena, etc.) as-is, just at a different path
              in this build — the OTT Hyperspace tile on /home links here
              instead of opening a separate tab. */}
          <Route path="/ott-hyperspace" element={<Landing />} />
          <Route path="/joinLive" element={<JoinLive />} />
          {/* A viewer following a ?state=&lga= link from a YouTube simulcast
              lands on /News. This build had no such route, so the catch-all
              below bounced them to "/" and they were asked to pick a region
              instead of seeing the broadcast they clicked through for. Same
              NewsDetail component already mounted at /News in the default
              build and at /cm-cni in the CM build — no new behaviour, just
              the path this domain was missing. */}
          <Route path="/News" element={<NewsDetail />} />
          {/* Demo POC, reachable in every build so it can be shown from
              whichever domain is to hand. Self-contained page. */}
          <Route path="/hover-detect" element={<DemoHoverDetect />} />
          <Route path="/payment/success" element={<PaymentSuccessful />} />
          {/* Not RoleGuard-wrapped (same as the CM module's own tree) — a
              Share Invite Link guest arrives here with no CM role at all
              (just ?invite=...&guest=true), and RoleGuard's role check ran
              before Broadcaster.js's own internal guest/invite handling
              ever got a chance to run, bouncing every invited guest to
              /home. Broadcaster.js itself already gates host-only actions
              internally via isGuest/isReporter. */}
          <Route path="/news-livestream" element={<Broadcaster />} />
          {/* VideoStore routes — this build had none, so a store owner/agent/
              branch manager logging in (LoginPage.js's handleLoginSuccess,
              StateLGAUi.js's "Dashboard" menu item) navigated to a path that
              didn't exist here and silently bounced back to "/" → "/home"
              via the catch-all below. Same routes as the default build. */}
          <Route path="/store" element={<MainLayout />} />
          <Route path="/store/agent" element={<Agent />} />
          <Route path="/store/admin" element={<RoleGuard allow={['store owner', 'branch manager']}><MainAdmin /></RoleGuard>} />
          <Route path="/store/buyer" element={<Buyer />} />
          <Route path="/store/cashier" element={<Cashier />} />
          {/* Restaurant routes — same gap as VideoStore above: a restaurant
              owner/manager/agent account had nowhere to land here either. */}
          <Route path="/restaurant/owner" element={<RestaurantOwner />} />
          <Route path="/restaurant/manager" element={<RestaurantManager />} />
          <Route path="/restaurant/agent" element={<RestaurantAgent />} />
          <Route path="/restaurant/buyer" element={<RestaurantBuyer />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (currentModule === "CM") {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<CNIlandingpage />} />
          <Route path="/CNI-landing" element={<CNIlandingpage />} />
          <Route path="/cmLanding" element={<CmLanding />} />
          <Route path="/cm" element={<AdminLoginFlow />} />
          <Route path="/GlobalAdmin" element={<GlobalDashboard />} />
          <Route path="/MasterAdmin" element={<MasterAdminDashboard />} />
          <Route path="/SuperAdmin" element={<SuperAdminDashboard />} />
          <Route path="/news-livestream" element={<Broadcaster />} />
          <Route path="/broadcaster" element={<Broadcaster />} />
          <Route path="/Viewer" element={<Viewer />} />
          <Route path="/cm-cni" element={<NewsDetail />} />
          {/* Same page as /cm-cni above, under the path a YouTube simulcast
              link points at (?state=&lga=). Without it the catch-all sent
              those viewers to "/" and the broadcast they clicked for was
              replaced by the region picker. */}
          <Route path="/News" element={<NewsDetail />} />
          {/* Same "OTT Hyperspace" hub the MAIN module's GEO button opens
              (landing.js's 6-card dashboard, which also embeds Broadcaster
              in its own "Impact" card via CardWithIframe.js) — added here so
              the CM module's GEO button lands on the identical page instead
              of a CM-specific stand-in. */}
          <Route path="/ott-hyperspace" element={<Landing />} />
          {/* Opened from the broadcast room's One to One / One to Many
              buttons (Broadcaster.js's handleOneToOneCall/handleOneToManyCall)
              — missing here meant they opened a tab that just fell through
              to the catch-all redirect below with nothing visible. */}
          <Route path="/OneToOneCall" element={<OneToOneCall />} />
          <Route path="/OneToManyCall" element={<OneToManyRoom />} />
          <Route path="/OneToOneRoom" element={<OneToOneRoom />} />
          {/* CM domain is limited to CNI + Join Live — /home must never show the full module screen */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/remote" element={<RemoteMobile />} />
          <Route path="/joinLive" element={<JoinLive />} />
          {/* Demo POC, reachable in every build so it can be shown from
              whichever domain is to hand. Self-contained page. */}
          <Route path="/hover-detect" element={<DemoHoverDetect />} />
          {/* Redirect all refresh or unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingVideo />} />
        <Route path="/home" element={<Landing />} />
        <Route path="/landingvideo" element={<LandingVideo />} />
        <Route path="/manager-login" element={<ManagerLoginPage />} />
        <Route path="/agent-login" element={<AgentLoginPage />} />
        <Route path="/manager-set-password" element={<ManagerSetPassword />} />
        <Route path="/agent-set-password" element={<AgentSetPassword />} />
        {/* VideoStore routes */}
        <Route path="/store" element={<MainLayout />} />
        <Route path="/store/agent" element={<Agent />} />
        {/* "branch manager" (capitalized "Branch Manager" from the real
            users-table role) is included alongside "store owner" — an
            older DashboardFlow.js path still routes that role here too;
            see project memory on the pending manager-ownership-check gap. */}
        <Route path="/store/admin" element={<RoleGuard allow={['store owner', 'branch manager']}><MainAdmin /></RoleGuard>} />
        <Route path="/store/buyer" element={<Buyer />} />
        <Route path="/store/cashier" element={<Cashier />} />
        {/* Legacy VideoStore redirects — keep so old links still work */}
        <Route path="/video-store" element={<Navigate to="/store" replace />} />
        <Route path="/main-admin" element={<Navigate to="/store/admin" replace />} />
        <Route path="/Agent" element={<Navigate to="/store/agent" replace />} />
        <Route path="/agent" element={<Navigate to="/store/agent" replace />} />
        <Route path="/buyer" element={<Navigate to="/store/buyer" replace />} />
        <Route path="/cashier" element={<Navigate to="/store/cashier" replace />} />
        <Route path="/create-camera-ip" element={<CameraIP />} />
        <Route path="/create-store" element={<CreateStore />} />
        <Route path="/create-video-store" element={<VisdeoStoreList />} />
        <Route path="/GlobalAdmin" element={<RoleGuard allow={CM_ROLES}><GlobalDashboard /></RoleGuard>} />
        <Route path="/MasterAdmin" element={<RoleGuard allow={CM_ROLES}><MasterAdminDashboard /></RoleGuard>} />
        <Route path="/SuperAdmin" element={<RoleGuard allow={CM_ROLES}><SuperAdminDashboard /></RoleGuard>} />
        <Route path="/joinLive" element={<JoinLive />} />
        {/* Not RoleGuard-wrapped — same reasoning, and the same fix, the
            MAIN module's own copy of this route already carries (see above):
            a Share Invite Link guest arrives with no CM role at all (just
            ?invite=...&guest=true, carried in the URL, not router state), so
            RoleGuard's role check ran first and bounced them to /home before
            Broadcaster.js's own invite handling could read the param. That
            was fixed for MAIN on 21 Sept but this build was left behind,
            which is why the broken invite link only ever showed up on
            geo.geomedium.com. Broadcaster.js still gates host-only actions
            internally via isGuest/isReporter. */}
        <Route path="/news-livestream" element={<Broadcaster />} />
        <Route path="/News" element={<NewsDetail />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/demo-3d-shelf" element={<Demo3DShelf />} />
        <Route path="/demo-flat-store" element={<DemoFlatStore />} />
        {/* Short client-facing URL — geo.geomedium.com/demo — back to the 3D
            version (the preferred direction); the flat (non-3D) version
            stays reachable at /demo-flat-store if it's wanted again later. */}
        <Route path="/demo" element={<Demo3DShelf />} />
        <Route path="/shelf-setup" element={<DemoShelfSetup />} />
        <Route path="/hover-detect" element={<DemoHoverDetect />} />

        {/* <Route path="/Movies" element={<FreeContent />} /> */}
        <Route path="/States" element={<States />} />
        <Route path="/NewsDetail" element={<NewsDetail />} />
        <Route path="/HomeLivestream" element={<HomeLivestream />} />
        <Route path="/LivestreamDetail/:option?" element={<LivestreamDetail />} />
        <Route path="/RealityShows" element={<RealityShows />} />
        <Route path="/Watchlist" element={<WatchlistSelect />} />
        <Route path="/Details" element={<RealityShowsDetails />} />
        <Route path="/cm" element={<AdminLoginFlow />} />
        <Route path="/cmLanding" element={<CmLanding />} />
        <Route path="/statepage" element={<Statepage />} />

        <Route path="/remote" element={<RemoteMobile />} />
        <Route path="/SocialnEngagement" element={<SocialnEngagement />} />
        <Route
          path="/SocialnEngagementDetails"
          element={<SocialnEngagementDetails />}
        />
        <Route path="/ComedyShows" element={<ComedyShows />} />
        <Route path="/ComedyShowsDetails" element={<ComedyShowsDetails />} />
        <Route path="/ListenSelect" element={<ListenSelect />} />
        <Route path="/Audio-Video-Contents" element={<AudioVideo />} />
        <Route path="/Music" element={<Music />} />
        <Route path="/World" element={<World />} />
        <Route path="/VOD" element={<VOD />} />
        <Route path="/broadcaster" element={<Broadcaster />} />
        <Route path="/Viewer" element={<Viewer />} />
        <Route path="/Comedy" element={<Comedy />} />
        <Route path="/ComedyShowsDetail" element={<ComedyShowsDetail />} />
        <Route path="/Drama" element={<Drama />} />
        <Route path="/DramaDetail" element={<DramaDetail />} />
        <Route path="/Series" element={<Series />} />
        <Route path="/SeriesDetail" element={<SeriesDetail />} />
        <Route path="/PhysicalStudio" element={<PhysicalStudio />} />
        <Route path="/VirtualStudio" element={<VirtualStudio />} />
        <Route path="/Contentcreate" element={<RoleGuard allow={CM_ROLES}><Contentcreate /></RoleGuard>} />
        <Route path="/ContentList" element={<RoleGuard allow={CM_ROLES}><ContentList /></RoleGuard>} />
        <Route path="/Dashboard" element={<RoleGuard allow={CM_ROLES}><Dashboard /></RoleGuard>} />
        <Route path="/LiveStream" element={<LiveStream />} />
        <Route path="/GeoMeet" element={<GeoMeet />} />
        <Route path="/OneToOneCall" element={<OneToOneCall />} />
        <Route path="/OneToManyCall" element={<OneToManyRoom />} />
        <Route path="/OneToOneRoom" element={<OneToOneRoom />} />
        <Route path="/restaurants" element={<Restaurants />} />
        <Route path="/bankarena" element={<Bankarena />} />
        <Route path="/Aboutus" element={<Aboutus />} />
        <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
        <Route path="/TermsConditions" element={<TermsConditions />} />
        <Route path="/Newsletter" element={<Newsletter />} />
        {/* Legacy Twilio-based pages — backend Twilio controller is disabled; redirect to the working Zego equivalents */}
        <Route path="/VideoCalling" element={<Navigate to="/OneToOneCall" replace />} />
        <Route path="/VideoConferencing" element={<Navigate to="/OneToManyCall" replace />} />
        <Route path="/Register-Restaurant" element={<RegisterRestaurant />} />
        <Route path="/restaurant/:id" element={<RestaurantDetails />} />
        <Route path="/menu-manager/:id" element={<MenuManager />} />
        <Route path="/order" element={<OrderSummary />} />
        <Route path="/invoice" element={<Invoice />} />
        <Route path="/flutter-wave" element={<Flutterwave />} />
        <Route path="/payment/success" element={<PaymentSuccessful />} />
        <Route path="/cryptopayment" element={<CryPtoPayment />} />
        <Route path="/order-tracking/:orderId" element={<OrderTracking />} />
        <Route path="/driver" element={<DriverDashboard />} />
        {/* Restaurant routes */}
        <Route path="/restaurant/agent" element={<RestaurantAgent />} />
        <Route path="/restaurant/owner" element={<RestaurantOwner />} />
        <Route path="/restaurant/manager" element={<RestaurantManager />} />
        <Route path="/restaurant/buyer" element={<RestaurantBuyer />} />
        {/* Legacy restaurant redirects */}
        <Route path="/restaurantagent" element={<Navigate to="/restaurant/agent" replace />} />
        <Route path="/restaurantowner" element={<Navigate to="/restaurant/owner" replace />} />
        <Route path="/restaurantmanager" element={<Navigate to="/restaurant/manager" replace />} />
        <Route path="/restaurantbuyer" element={<Navigate to="/restaurant/buyer" replace />} />
        <Route path="/StateLgawel" element={<StateLgawel />} />
        <Route path="/CNI-landing" element={<CNIlandingpage />} />
        <Route path="/cm-cni" element={<NewsDetail />} />
        {/* Issue 1 — catch-all so hard-refresh on any route falls back to /home */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
